/**
 * SuperAdmin ads mass-push (SA-3). Products stay club-targeted; no tenant leak.
 */
import React, { useEffect, useState } from 'react';
import { Megaphone, AlertTriangle, Loader2 } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../i18n';
import { interpolate } from '../locales/superAdmin';

const SuperAdminAdsTab = () => {
    const { t } = useI18n();
    const adsText = t.superAdmin.ads;
    const [products, setProducts] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedClubs, setSelectedClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pushing, setPushing] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        description: '',
        imageUrl: '',
        productUrl: '',
        price: '',
        salePrice: '',
        isActive: true
    });
    const [message, setMessage] = useState({ type: '', text: '' });

    const load = async () => {
        setLoading(true);
        try {
            const [prodRes, clubRes] = await Promise.all([
                apiFetch('/api/superadmin/ads/products'),
                apiFetch('/api/superadmin/ads/eligible-clubs')
            ]);
            if (prodRes.ok) setProducts(await prodRes.json());
            if (clubRes.ok) setClubs(await clubRes.json());
        } catch (err) {
            setMessage({ type: 'error', text: err.message || adsText.loadFailed });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggleId = (list, setList, id) => {
        setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
    };

    const startEdit = (product, event) => {
        event.preventDefault();
        event.stopPropagation();
        setEditing(product);
        setEditForm({
            name: product.name || '',
            description: product.description || '',
            imageUrl: product.image_url || '',
            productUrl: product.product_url || '',
            price: product.price ?? '',
            salePrice: product.sale_price ?? '',
            isActive: product.is_active !== 0 && product.is_active !== false
        });
    };

    const handleSaveEdit = async () => {
        if (!editing) return;
        setSavingEdit(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await apiFetch(`/api/superadmin/ads/products/${editing.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: editForm.name,
                    description: editForm.description,
                    imageUrl: editForm.imageUrl,
                    productUrl: editForm.productUrl,
                    price: editForm.price === '' ? null : Number(editForm.price),
                    salePrice: editForm.salePrice === '' ? null : Number(editForm.salePrice),
                    category: editing.category,
                    tags: editing.tags,
                    priority: editing.priority,
                    isActive: editForm.isActive,
                    stockStatus: editing.stock_status || 'in_stock'
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || adsText.saveFailed);
            setMessage({ type: 'success', text: adsText.productUpdated });
            setEditing(null);
            await load();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || adsText.saveFailed });
        } finally {
            setSavingEdit(false);
        }
    };

    const handlePush = async () => {
        if (selectedProducts.length === 0 || selectedClubs.length === 0) {
            setMessage({ type: 'error', text: adsText.selectionRequired });
            return;
        }
        setPushing(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await apiFetch('/api/superadmin/ads/push', {
                method: 'POST',
                body: JSON.stringify({ productIds: selectedProducts, clubIds: selectedClubs })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || adsText.publishFailed);
            setMessage({
                type: 'success',
                text: interpolate(adsText.publishSuccess, { count: data.assigned?.length || 0 })
                    + (data.skippedClubs?.length
                        ? ` ${interpolate(adsText.skippedClubs, { clubs: data.skippedClubs.map((c) => c.name).join(', ') })}`
                        : '')
            });
            await load();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || adsText.publishFailed });
        } finally {
            setPushing(false);
        }
    };

    if (loading) {
        return <p style={{ color: '#8b949e' }}><Loader2 size={16} className="spin" /> {adsText.loading}</p>;
    }

    return (
        <div>
            <h3 style={{ color: '#f0f6fc', marginBottom: '1rem' }}>
                <Megaphone size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                {adsText.title}
            </h3>
            <p style={{ color: '#8b949e', marginBottom: '1rem' }}>
                {adsText.description}
                <code style={{ margin: '0 0.25rem' }}>ads_admin</code>.
            </p>
            <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                color: '#f59e0b',
                marginBottom: '1.5rem'
            }}>
                <AlertTriangle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                {adsText.betaWarning}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                    <h4 style={{ color: '#f0f6fc' }}>{adsText.products}</h4>
                    {products.length === 0 && <p style={{ color: '#8b949e' }}>{adsText.noProducts}</p>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '360px', overflow: 'auto' }}>
                        {products.map((p) => (
                            <label key={p.id} style={{
                                display: 'flex',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                background: 'rgba(0,0,0,0.25)',
                                border: '1px solid #30363d',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                alignItems: 'flex-start'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={selectedProducts.includes(p.id)}
                                    onChange={() => toggleId(selectedProducts, setSelectedProducts, p.id)}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ color: '#f0f6fc' }}>{p.name}</div>
                                    <div style={{ color: '#8b949e', fontSize: '0.8rem' }}>
                                        {p.sponsor_name || adsText.noSponsor}
                                        {p.targetedClubs?.length
                                            ? ` · ${p.targetedClubs.map((c) => c.name).join(', ')}`
                                            : ` · ${adsText.noTargeting}`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={(event) => startEdit(p, event)}
                                >
                                    {adsText.edit}
                                </button>
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <h4 style={{ color: '#f0f6fc' }}>{adsText.clubsTitle}</h4>
                    {clubs.length === 0 && <p style={{ color: '#8b949e' }}>{adsText.noClubs}</p>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '360px', overflow: 'auto' }}>
                        {clubs.map((c) => (
                            <label key={c.id} style={{
                                display: 'flex',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                background: 'rgba(0,0,0,0.25)',
                                border: '1px solid #30363d',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={selectedClubs.includes(c.id)}
                                    onChange={() => toggleId(selectedClubs, setSelectedClubs, c.id)}
                                />
                                <div>
                                    <div style={{ color: '#f0f6fc' }}>{c.name}</div>
                                    <div style={{ color: '#8b949e', fontSize: '0.8rem' }}>/{c.slug}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {editing && (
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid #30363d',
                    borderRadius: '8px'
                }}>
                    <h4 style={{ color: '#f0f6fc', marginTop: 0 }}>{adsText.editProduct}</h4>
                    <p style={{ color: '#8b949e', fontSize: '0.85rem' }}>
                        {adsText.editHelp}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem' }}>
                            {adsText.name}
                            <input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem' }}>
                            {adsText.productUrl}
                            <input
                                value={editForm.productUrl}
                                onChange={(e) => setEditForm({ ...editForm, productUrl: e.target.value })}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
                            {adsText.imageUrl}
                            <input
                                value={editForm.imageUrl}
                                onChange={(e) => setEditForm({ ...editForm, imageUrl: e.target.value })}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem' }}>
                            {adsText.price}
                            <input
                                type="number"
                                value={editForm.price}
                                onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem' }}>
                            {adsText.salePrice}
                            <input
                                type="number"
                                value={editForm.salePrice}
                                onChange={(e) => setEditForm({ ...editForm, salePrice: e.target.value })}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
                            {adsText.productDescription}
                            <textarea
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                rows={3}
                                style={{ width: '100%', marginTop: '0.25rem' }}
                            />
                        </label>
                        <label style={{ color: '#c9d1d9', fontSize: '0.85rem' }}>
                            <input
                                type="checkbox"
                                checked={editForm.isActive}
                                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                            /> {adsText.active}
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                        <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                            {savingEdit ? <Loader2 size={16} className="spin" /> : null} {adsText.saveProduct}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={savingEdit}>
                            {adsText.cancel}
                        </button>
                    </div>
                </div>
            )}

            <button className="btn-primary" onClick={handlePush} disabled={pushing}>
                {pushing ? <Loader2 size={16} className="spin" /> : <Megaphone size={16} />}
                {' '}{adsText.publish}
            </button>
            {message.text && (
                <p style={{ color: message.type === 'error' ? '#f85149' : '#3fb950', marginTop: '0.75rem' }}>
                    {message.text}
                </p>
            )}
        </div>
    );
};

export default SuperAdminAdsTab;
